import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

/**
 * Cmd+K / Ctrl+K command palette — keyboard-driven jump to any nav route.
 *
 * Pulls a flat list of navigable destinations gated by role and feeds
 * them through cmdk's fuzzy filter. Replaces the old "scroll the
 * sidebar to find the thing" pattern; complements the avatar dropdown
 * and the new collapsible sections without depending on either.
 *
 * The route list is duplicated from AppShell.tsx on purpose — the
 * sidebar carries icons + section grouping that don't translate
 * cleanly into a flat search list. Adding a new admin route requires
 * touching both, but that's a small ergonomic price for the cmdk
 * payload weight (12 kB gzipped) staying tight.
 */

interface PaletteItem {
  to: string;
  labelKey: string;
  /** Section label shown as the cmdk group heading. */
  groupKey: string;
  /** Optional plain text label fallback for missing i18n keys. */
  fallbackLabel?: string;
  /** Roles this item is visible to. Empty = everyone. */
  roles?: string[];
}

const ELEVATED = ['TENANT_ADMIN', 'SALES_MANAGER', 'PLATFORM_ADMIN'];
const ALL = ['TENANT_ADMIN', 'SALES_MANAGER', 'PLATFORM_ADMIN', 'SALES_REPRESENTATIVE', 'PRE_SALES'];

const PALETTE_ITEMS: PaletteItem[] = [
  // Action items
  { to: '/inbox', labelKey: 'nav.inbox', groupKey: 'palette.group_workspace', fallbackLabel: 'Inbox' },
  { to: '/conversations', labelKey: 'nav.conversations', groupKey: 'palette.group_workspace' },
  { to: '/tasks', labelKey: 'nav.tasks', groupKey: 'palette.group_workspace' },
  { to: '/dashboard', labelKey: 'nav.dashboard', groupKey: 'palette.group_workspace', fallbackLabel: 'Dashboard' },
  { to: '/portfolio', labelKey: 'nav.portfolio', groupKey: 'palette.group_workspace', roles: ELEVATED },
  { to: '/cockpit/sdr', labelKey: 'workspace.nav_sdr', groupKey: 'palette.group_workspace' },
  { to: '/handoff-inbox', labelKey: 'handoff.inbox_title', groupKey: 'palette.group_workspace' },

  // Records
  { to: '/campaigns', labelKey: 'nav.campaigns', groupKey: 'nav.section.records' },
  { to: '/leads', labelKey: 'nav.leads', groupKey: 'nav.section.records' },
  { to: '/accounts', labelKey: 'nav.accounts', groupKey: 'nav.section.records' },
  { to: '/opportunities', labelKey: 'nav.opportunities', groupKey: 'nav.section.records' },
  { to: '/customers', labelKey: 'nav.customers', groupKey: 'nav.section.records' },
  { to: '/search', labelKey: 'nav.search', groupKey: 'nav.section.records' },

  // Analytics
  { to: '/analytics', labelKey: 'nav.analytics', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/analytics/channels', labelKey: 'nav.channels', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/analytics/attribution', labelKey: 'nav.attribution', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/analytics/mktg-sales-align', labelKey: 'nav.mktgSalesAlign', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/analytics/funnel', labelKey: 'nav.funnel', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/cockpit/rep', labelKey: 'nav.cockpitRep', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/cockpit/manager', labelKey: 'nav.cockpitManager', groupKey: 'nav.section.analytics', roles: ELEVATED },
  { to: '/cockpit/board', labelKey: 'nav.cockpitBoard', groupKey: 'nav.section.analytics', roles: ELEVATED },

  // Admin (elevated roles only)
  { to: '/admin/agents', labelKey: 'nav.agents', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/text-agents', labelKey: 'nav.textAgents', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/chat-widget', labelKey: 'nav.chatWidget', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/chains', labelKey: 'nav.chains', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/workflow-runs', labelKey: 'nav.workflowRuns', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/sequences', labelKey: 'nav.sequences', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/email-pools', labelKey: 'nav.emailPools', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/email-domain-setup', labelKey: 'nav.emailDomains', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/process-map', labelKey: 'nav.processMap', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/lead-qualification', labelKey: 'nav.leadQualification', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/qualification-banks', labelKey: 'nav.qualificationBanks', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/qualification/conversations', labelKey: 'nav.qualificationConversations', groupKey: 'nav.section.admin' },
  { to: '/admin/playbooks', labelKey: 'playbook.nav', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/commission-tiers', labelKey: 'commissionTiers.nav', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/revenue-cadence', labelKey: 'revenueCadence.nav', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/revenue-meeting', labelKey: 'revenueCadence.meeting_title', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/cockpit/am', labelKey: 'workspace.nav_am', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/lead-capture-forms', labelKey: 'nav.captureForms', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/imports', labelKey: 'nav.imports', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/dnc', labelKey: 'nav.dnc', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/voice-credentials', labelKey: 'nav.voiceCreds', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/enrichment', labelKey: 'nav.enrichment', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/process-templates', labelKey: 'nav.templates', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/products', labelKey: 'nav.products', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/billing', labelKey: 'nav.billing', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/api-builder', labelKey: 'nav.apiBuilder', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/webhooks', labelKey: 'nav.webhooks', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/target-accounts', labelKey: 'nav.targetAccounts', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/segmentation', labelKey: 'nav.segmentation', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/settings', labelKey: 'nav.settings', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/users', labelKey: 'nav.users', groupKey: 'nav.section.admin', roles: ELEVATED },
  { to: '/admin/permissions', labelKey: 'nav.permissions', groupKey: 'nav.section.admin', roles: ELEVATED },

  // Personal (always visible)
  { to: '/account/change-password', labelKey: 'userMenu.changePassword', groupKey: 'palette.group_personal', roles: ALL },
  { to: '/account/email-settings', labelKey: 'userMenu.emailSettings', groupKey: 'palette.group_personal', roles: ALL },
  { to: '/account/commission-payouts', labelKey: 'commissionPayouts.nav', groupKey: 'palette.group_personal' },
];

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Cmd+K (mac) / Ctrl+K (everywhere else) toggles. ESC closes (cmdk
  // gives this for free inside its dialog).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function go(to: string) {
    setOpen(false);
    setQuery('');
    navigate(to);
  }

  const items = PALETTE_ITEMS.filter((item) => {
    if (!item.roles) return true;
    return role ? item.roles.includes(role) : false;
  });

  // Group by groupKey for cmdk's <Command.Group> rendering.
  const grouped = items.reduce<Record<string, PaletteItem[]>>((acc, item) => {
    (acc[item.groupKey] ??= []).push(item);
    return acc;
  }, {});

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 p-4 pt-[15vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-[10px] border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] shadow-[var(--shadow-lop-lg)]"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label={t('palette.label')} shouldFilter>
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder={t('palette.placeholder')}
            className="w-full border-b border-[var(--color-lop-border)] bg-transparent px-4 py-3 text-sm text-[var(--color-lop-text-primary)] outline-none placeholder:text-[var(--color-lop-text-dim)]"
            autoFocus
          />
          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="px-3 py-6 text-center text-sm text-[var(--color-lop-text-muted)]">
              {t('palette.empty')}
            </Command.Empty>
            {Object.entries(grouped).map(([groupKey, groupItems]) => (
              <Command.Group
                key={groupKey}
                heading={t(groupKey)}
                className="mb-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.15em] [&_[cmdk-group-heading]]:text-[var(--color-lop-text-muted)]"
              >
                {groupItems.map((item) => (
                  <Command.Item
                    key={item.to}
                    value={`${t(item.labelKey, item.fallbackLabel ?? '')} ${item.to}`}
                    onSelect={() => go(item.to)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm text-[var(--color-lop-text-secondary)]',
                      'aria-selected:bg-[var(--color-lop-bg-card-hi)] aria-selected:text-[var(--color-lop-teal)] aria-selected:shadow-[inset_2px_0_0_var(--color-lop-teal)]',
                    )}
                  >
                    <span>{t(item.labelKey, item.fallbackLabel ?? item.labelKey)}</span>
                    <span className="font-mono text-[11px] text-[var(--color-lop-text-dim)]">
                      {item.to}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
          <div className="flex items-center justify-between border-t border-[var(--color-lop-border)] bg-[var(--color-lop-bg-mid)] px-3 py-2 text-[11px] text-[var(--color-lop-text-muted)]">
            <span>{t('palette.hint_navigate')}</span>
            <span>
              <kbd className="rounded border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-1.5 py-0.5 font-mono">↵</kbd>{' '}
              {t('palette.hint_select')}
              {' · '}
              <kbd className="rounded border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-1.5 py-0.5 font-mono">esc</kbd>{' '}
              {t('palette.hint_close')}
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
